import type { Step as RcStep } from '@sb/remote-control/types';

import type { Diagnostic, ValidationRule } from '../../types';
import {
  catchActionRequiredToDiagnostic,
  createStepRule,
} from '../../util/ruleHelpers';
import {
  validateConditional,
  validateConditionalIsComplete,
} from '../../util/validators';

/**
 * Validate ElseIf's `condition` field. Block-connection ("ElseIf must follow
 * an If or another ElseIf") is handled by the `conditional.orphanedElseBlock`
 * common rule in `Suppressed.ts`, which also accounts for suppressed
 * siblings — so this rule only covers the condition contents.
 *
 * Unlike `If`, this rule is not built on `CanonicalStepRuleBase`, so it gets no
 * runner-schema cross-check — and `ElseIf` has no entry in `runnerSchemas`
 * either. Completeness therefore has to be asserted here, or a half-configured
 * ElseIf leaves Play enabled and fails at the first evaluation instead.
 */
export const ElseIfValidation: ValidationRule = createStepRule({
  id: 'steps.ElseIf',
  stepKinds: ['ElseIf'],
  run: ({ routine, stepId, warn }): Diagnostic[] => {
    const stepCfg = routine.stepConfigurations[stepId];
    if (!stepCfg) return [];

    const args = stepCfg.args as RcStep.Arguments & { condition?: string };
    if (args.condition == null) return [];

    try {
      const conditions = JSON.parse(args.condition || '[]');
      warn(validateConditional(conditions, routine));
      validateConditionalIsComplete(conditions);

      return [];
    } catch (err) {
      return catchActionRequiredToDiagnostic(err, stepId, 'steps.ElseIf');
    }
  },
});
